import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';

import { renderTools } from '../../helpers/RenderTools';
import type {
  CssClassMap,
  StaticTeasers,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-current-topic',
  styleUrl: 'cms-current-topic.scss',
  shadow: true,
})
export class CmsCurrentTopic {
  private readonly imageSizes = `
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 30vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 30vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 50vw,
(min-width: 1280px) 390px,
 100vw`;

  private readonly lastImageSizes = `
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 50vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 50vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 100vw,
(min-width: 1280px) 390px,
 100vw`;

  private topicsResults: StaticTeasers[];

  /**
   * set the headline
   */
  @Prop() headline: '';

  /**
   * set the topics content
   */
  @Prop({ mutable: true }) topics: StaticTeasers | string;

  /**
   * set the targetAllTopics url
   */
  @Prop() targetAllTopics: '';

  /**
   * set the targetAllTopicsLabel label
   */
  @Prop() targetAllTopicsLabel: '';

  /**
   * set the hasBackground
   */
  @Prop({ mutable: true }) hasBackground: boolean;

  @Element() hostElement: HTMLElement;

  @Watch('topics')
  private topicsWatcher(newValue: string | StaticTeasers) {
    if (newValue) {
      this.topicsResults =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  private topicsWrapperClassMap(): CssClassMap {
    return {
      'container-wrapper-inner': true,
      'static-teaser': true,
    };
  }

  private mapTeasers(teaserResult: StaticTeasers[]): (string | undefined)[] {
    return teaserResult?.map((teaser: StaticTeasers, index: number) => {
      const imageSizes = index === 2 ? this.lastImageSizes : this.imageSizes;
      const updatedTeaser = {
        ...teaser,
        imageSizes,
        imageClassName: 'current-topic-image',
      };

      return renderTools.getTeaserHtml(updatedTeaser);
    });
  }

  connectedCallback() {
    if (this.topics === undefined) {
      return;
    }

    this.topicsWatcher(this.topics);
  }

  componentWillLoad() {
    const bg = this.hasBackground;

    if (bg) {
      this.hostElement.classList.add('has-bg');
      this.hostElement.classList.add('cms-dark-theme');
    }
  }

  render() {
    return (
      <Host
        style={
          this.hasBackground
            ? {
                backgroundColor:
                  'var(--cms-semanticColors-container-inverted-weak-background)',
                padding: '2rem 0',
              }
            : null
        }
      >
        <div class={this.topicsWrapperClassMap()}>
          <cms-headline
            style={
              this.hasBackground
                ? {
                    '--cms-theme-text-color':
                      'var(--cms-semanticColors-base-inverted-strong-text)',
                  }
                : null
            }
            headlineText={this.headline}
            headlineTag={'h2'}
            headlinePosition='center'
            headlineClass='headline'
            isAdaptive={true}
          />
          <div class={'teaser-content topic-content'}>
            {this.mapTeasers(this.topicsResults)}
          </div>
          {this.targetAllTopicsLabel === '' ? null : (
            <div class={'all-topics-wrapper'}>
              <cms-link
                style={
                  this.hasBackground
                    ? {
                        '--cms-link-text-color':
                          'var(--cms-semanticColors-base-inverted-strong-text)',
                      }
                    : null
                }
                linkTitle={this.targetAllTopicsLabel}
                type='standalone'
                size='lg'
                href={this.targetAllTopics}
                content={this.targetAllTopicsLabel}
              />
            </div>
          )}
        </div>
      </Host>
    );
  }
}
